import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, ValidationErrors } from '@angular/forms';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES } from '@constants/constants';
import { enumFileExtension } from '@enum/enumFileExtension';
import { enumMimeType } from '@enum/enumMimeType';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-document-browse-button',
  templateUrl: './document-browse-button.component.html',
  styleUrls: ['./document-browse-button.component.scss'],
})
export class DocumentBrowseButtonComponent implements OnInit, OnChanges {
  @Input() public id: string = 'app-document-browse-button';
  @Input() public changeCounter: number;
  @Input() public isDisabled: boolean = false;
  @Input() public fileMimeIgnoreList: enumMimeType[] = [];
  @Input() public fileExtensionIgnoreList: enumFileExtension[] = [];
  @Input() public preSelectedFile: File | null = null;

  @Output() public browse: EventEmitter<File> = new EventEmitter<File>();
  @Output() public replace: EventEmitter<void> = new EventEmitter<void>();
  @Output() public fail: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('inputElement', { static: false }) public inputElement: ElementRef;

  public isShowInvalidFileToast: boolean = false;
  public allowedFileMimes: string = '';
  public fileControl: UntypedFormControl = new UntypedFormControl(null);

  public errorMessage: string = '';

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    this.allowedFileMimes = ALLOWED_FILE_MIMES.filter((fm) => !this.fileMimeIgnoreList.includes(fm)).join(',');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.changeCounter && !changes.changeCounter.firstChange) {
      if (changes.changeCounter.currentValue !== changes.changeCounter.previousValue) {
        this.setIsShowInvalidFileToast(false);
      }
    }
  }

  public onBrowse() {
    if (this.preSelectedFile) {
      this.browse.emit(this.preSelectedFile);
      return;
    }
    this.inputElement.nativeElement.click();
  }

  public handleFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      this.uploadFile(files[0]);
    }
  }

  public onCloseInvalidFileToast() {
    this.setIsShowInvalidFileToast(false);
  }

  public onReplace() {
    this.setIsShowInvalidFileToast(false);
    this.replace.emit();
  }

  private uploadFile(file: File): void {
    this.clearFile();
    this.fileControl.patchValue(file);
    this.fileControl.markAsTouched();

    this.updateInputErrors(file);

    if (this.fileControl.valid) {
      this.browse.emit(file);
    } else {
      this.fail.emit();
      this.setIsShowInvalidFileToast(true);
    }
    this.inputElement.nativeElement.value = null;
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }

  private setIsShowInvalidFileToast(value: boolean) {
    this.isShowInvalidFileToast = value;
  }

  private updateInputErrors(file: File) {
    const allowedFileExtensions = ALLOWED_FILE_EXTENSIONS.filter((fe) => !this.fileExtensionIgnoreList.includes(fe));
    const extensionError = this.fileExtensionsValidator(file, allowedFileExtensions);
    const sizeError = this.maxFileSizeValidator(file, environment.files.default_file_size_limit); // 10 MB limit

    if (extensionError) {
      this.fileControl.setErrors(extensionError);
      this.errorMessage = this.translateService.instant('FORM_ERROR.FILE_EXTENSION_NOT_SUPPORTED', { fileName: file.name });
    } else if (sizeError) {
      this.fileControl.setErrors(sizeError);
      this.errorMessage = this.translateService.instant('FORM_ERROR.FILE_SIZE_EXCEEDS_LIMIT', { fileName: file.name });
    } else {
      this.fileControl.setValue(file);
      this.fileControl.setErrors(null);
      this.errorMessage = '';
    }
  }

  private fileExtensionsValidator(file: File, allowedExtensions: string[]): ValidationErrors | null {
    const extension = file.name.split('.').pop()?.toLowerCase() as string;

    return allowedExtensions.includes(extension) ? null : { fileExtensionNotAllowed: true };
  }

  private maxFileSizeValidator(file: File, maxFileSizeMB: number): ValidationErrors | null {
    const fileSizeMB = file.size / 1024 / 1024;

    return fileSizeMB > maxFileSizeMB
      ? { maxFileSize: { currentSize: Math.round(fileSizeMB * 100) / 100, maxFileSize: maxFileSizeMB } }
      : null;
  }
}
